import { useCallback, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import qs, { ParsedQs } from 'qs';
import { UseUserQuery_user as User } from '../../generated/types/UseUserQuery';
import { MIN_AFFIRM_PRICE, MAX_AFFIRM_PRICE } from '../../constants';
import { formatPriceAsCents } from '../../lib/formatPriceAsCents';
import { useCountry } from '../../components/CountrySelector';
import { useUser } from '../useUser';
import { RestAddressAttributes } from '../useAddress';
import { OrderSummary, useOrderSummary } from '../useOrderSummary';
import { OrderItem } from '../useOrder';
import { OrderState } from '../useUpdateOrder/useUpdateOrder.rest';
import { usePlaceAffirmSecondPaymentOrderMutation } from './usePlaceAffirmSecondPaymentOrderMutation';

type AffirmCheckoutArgs = {
  user: User;
  orderSummary: OrderSummary;
  orderItems: OrderItem[];
  shippingAddress: RestAddressAttributes;
};

interface WithPS {
  ps: string;
}

type Params = WithPS & ParsedQs;

export type AffirmSecondPaymentResponse = {
  billing_address: {
    city: string;
    line1: string;
    line2: string;
    state: string;
    zipcode: string;
  };
  callback_id: string;
  cardholder_name: string;
  checkout_token: string;
  created: string;
  cvv: string;
  expiration: string;
  id: string;
  number: string;
};

const buildAffirmSecondPaymentData = ({
  user,
  orderSummary,
  orderItems,
  shippingAddress
}: AffirmCheckoutArgs) => ({
  shipping: {
    name: {
      first: shippingAddress.firstName,
      last: shippingAddress.lastName
    },
    address: {
      line1: shippingAddress.addressLine1,
      line2: shippingAddress.addressLine2,
      city: shippingAddress.city,
      state: shippingAddress.state,
      zipcode: shippingAddress.postalCode,
      country: shippingAddress.countryCode
    },
    phone_number: shippingAddress.phone,
    email: user.email
  },
  items: orderItems.map(orderItem => ({
    display_name: orderItem.attributes.details.name,
    sku: orderItem.attributes.skuId,
    unit_price: formatPriceAsCents(orderItem.attributes.prices.totalPrice.price),
    qty: orderItem.attributes.quantity,
    item_image_url: orderItem.attributes.images.medium,
    item_url: window.location.origin + orderItem.attributes.productHref
  })),
  shipping_amount: formatPriceAsCents(
    orderSummary.attributes.paymentSummary.shippingAndPackaging.price
  ),
  tax_amount:
    orderSummary.attributes.paymentSummary.salesTax &&
    formatPriceAsCents(orderSummary.attributes.paymentSummary.salesTax.price),
  total: formatPriceAsCents(orderSummary.attributes.paymentSummary.payToday.price),
  metadata: {
    mode: 'modal'
  }
});

export const useAffirmSecondPayment = () => {
  const { orderNumber } = useParams<{ orderNumber: string }>();
  const { orderSummary, orderItems } = useOrderSummary(orderNumber);
  const location = useLocation();
  const { country } = useCountry();
  const { user } = useUser();
  const { ps: ps = '' } = qs.parse(location.search.slice(1)) as Params;

  const isBillFailureOrPendingPayment = useMemo(
    () =>
      orderItems &&
      orderItems.some(({ attributes: { status } }) =>
        ['payment_pending', 'bill_failure'].includes(status)
      ),
    [orderItems]
  );

  const affirmRemainingBalance = useMemo(
    () =>
      orderItems &&
      orderItems
        .filter(item => ['payment_pending', 'bill_failure'].includes(item.attributes.status))
        .reduce(
          (amount, item) => amount + Number(item.attributes.prices.totalBalancePayment.price),
          0
        ),
    [orderItems]
  );

  const [placeOrder] = usePlaceAffirmSecondPaymentOrderMutation();

  const paymentSummary = orderSummary?.attributes.paymentSummary;
  const shippingAddress = orderSummary?.attributes.address;
  const orderState = orderSummary?.attributes.orderState;

  const priceToPayWithAffirm =
    orderState === OrderState.Default ? affirmRemainingBalance : paymentSummary?.payToday.price;

  const placeAffirmSecondPayment = useCallback((): ReturnType<typeof placeOrder> => {
    if (!shippingAddress)
      return Promise.reject('placeAffirmSecondPayment: no shipping address selected');
    if (!orderSummary) return Promise.reject('placeAffirmSecondPayment: no orderSummary');
    if (!orderItems) return Promise.reject('placeAffirmSecondPayment: no orderItems');

    return new Promise((resolve, reject) => {
      window.affirm.checkout.open_vcn({
        checkout_data: buildAffirmSecondPaymentData({
          user,
          orderSummary: orderSummary as OrderSummary,
          orderItems: orderItems as OrderItem[],
          shippingAddress
        }),
        success: async (response: AffirmSecondPaymentResponse) => {
          try {
            const result = await placeOrder(response);
            resolve(result);
          } catch (error) {
            reject(error);
          }
        },
        error: (error: Error) => {
          reject(error);
        }
      });
    });
  }, [shippingAddress, user, orderSummary, orderItems, placeOrder]);

  const isAffirmAvailable =
    isBillFailureOrPendingPayment &&
    country.alpha2Code === 'US' &&
    shippingAddress &&
    shippingAddress.countryCode === 'US' &&
    paymentSummary &&
    paymentSummary.remainingBalance.currency === 'usd' &&
    priceToPayWithAffirm &&
    priceToPayWithAffirm >= MIN_AFFIRM_PRICE &&
    priceToPayWithAffirm <= MAX_AFFIRM_PRICE;

  return {
    placeAffirmSecondPayment,
    isAffirmQueryParamPresent: ps === 'affirm',
    isAffirmAvailable
  };
};
